import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import Alert from './Components/CommonComponents/Alert';
import Header from "./Components/CommonComponents/Header";

import Footer from './Components/Partials/Footer';
import Spinner from './Components/Partials/Loading/Spinner';

import { initialPasskeyReauthCheck } from './Services/method';

import useDevtoolsDetector from './hooks/useDevtoolsDetector';

import { getAnalyticsPageContext, getAnalyticsUserContext } from './Utils/Analytics';
import { resolveIpCountryDefault } from './Utils/IpCountry';

import {
  debugPaymentBridge, FORWARD_TO_IFRAME_MESSAGE, PAYMENT_COMPLETE_MESSAGE, PAYMENT_RESULT_CHANNEL,
  PAYMENT_RESULT_STORAGE_KEY
} from './Utils/PaymentReturn';

export default function App() {
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useDevtoolsDetector();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        resolveIpCountryDefault();
        await initialPasskeyReauthCheck();
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
    const handleReauth = () => {
      setAlert({
        show: true,
        message: "Please verify your identity to continue",
        type: "info",
        persistent: true
      });
    };

    const handleSuccess = () => {
      setAlert({ show: true, message: "Verified!", type: "success" });
    };

    document.addEventListener("passkey-reauth-required", handleReauth);
    document.addEventListener("passkey-reauth-success", handleSuccess);

    return () => {
      document.removeEventListener("passkey-reauth-required", handleReauth);
      document.removeEventListener("passkey-reauth-success", handleSuccess);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...getAnalyticsUserContext(),
      event: "virtual_page_view",
      custom_event_name: "virtual_page_view",
      ecommerce: null,
      ...getAnalyticsPageContext(),
      page_path: `${location.pathname}${location.search}${location.hash}`,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        })
        .catch(() => {
          // Ignore service worker cleanup failures.
        });
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const isEmbedded = window.self !== window.top;
    let lastRelayedAt = null;

    debugPaymentBridge("payment bridge listeners mounted", { isEmbedded, href: window.location.href });

    // The iframe is the only bankco.com context the popup's storage/
    // BroadcastChannel signal can reach directly (localStorage and
    // BroadcastChannel are same-origin only). To notify the integrator's
    // own top-level page, the iframe has to explicitly relay the result
    // up via window.parent.postMessage — the integrator's host page adds
    // its own listener for PAYU_PAYMENT_COMPLETE to react (redirect,
    // close a modal, refetch order state, etc).
    const relayToHost = (message) => {
      if (!isEmbedded) return;
      if (!message?.completedAt || message.completedAt === lastRelayedAt) {
        debugPaymentBridge("relayToHost skipped (duplicate or no completedAt)", message);
        return;
      }
      lastRelayedAt = message.completedAt;
      window.parent.postMessage({ type: PAYMENT_COMPLETE_MESSAGE, ...message }, "*");
      debugPaymentBridge("relayToHost -> window.parent.postMessage", message);
    };

    const normalizeMessage = (event) => {
      if (!event?.data) return null;
      if (event.data?.type === FORWARD_TO_IFRAME_MESSAGE) {
        return event.data.payload;
      }
      return event.data;
    };

    const getRedirectTarget = (message) => {
      const rawOrderPath = String(message.orderPath || "").trim();
      const rawOrderUrl = String(message.orderUrl || "").trim();

      if (rawOrderUrl) {
        try {
          const parsedUrl = new URL(rawOrderUrl, window.location.origin);
          if (parsedUrl.origin !== window.location.origin) {
            return { external: rawOrderUrl };
          }
          return { path: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` };
        } catch {
          return { path: rawOrderPath };
        }
      }

      return { path: rawOrderPath };
    };

    const navigateToMessage = (message, via) => {
      if (!message || message.type !== PAYMENT_COMPLETE_MESSAGE) {
        debugPaymentBridge(`navigateToMessage ignored (via ${via})`, message);
        return;
      }

      debugPaymentBridge(`navigateToMessage (via ${via})`, message);
      relayToHost(message);

      const target = getRedirectTarget(message);

      if (target.external) {
        debugPaymentBridge("navigating (external)", target.external);
        window.location.assign(target.external);
        return;
      }

      if (target.path) {
        debugPaymentBridge("navigating (internal)", target.path);
        navigate(target.path, { replace: true });
      } else {
        debugPaymentBridge("navigateToMessage: no usable target path", message);
      }
    };

    const handleMessage = (event) => {
      const message = normalizeMessage(event);
      navigateToMessage(message, `message from ${event.origin}`);
    };

    const handleStorage = (event) => {
      if (event.key !== PAYMENT_RESULT_STORAGE_KEY || !event.newValue) return;
      try {
        const message = JSON.parse(event.newValue);
        navigateToMessage(message, "storage");
      } catch {
        // ignore invalid storage payload
      }
    };

    let channel;
    try {
      channel = new BroadcastChannel(PAYMENT_RESULT_CHANNEL);
      channel.onmessage = (event) => navigateToMessage(event.data, "broadcastchannel");
      debugPaymentBridge("BroadcastChannel listener attached", { channel: PAYMENT_RESULT_CHANNEL });
    } catch (e) {
      // BroadcastChannel unsupported (e.g. old Safari) — storage/message
      // listeners below still cover this case.
      channel = null;
      debugPaymentBridge("BroadcastChannel unsupported", String(e));
    }

    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, [navigate]);

  if (isLoading) {
    return <div><Spinner /></div>;
  }

  return (
    <div className="appShell">
      <Alert
        message={alert.message}
        show={alert.show}
        setShowAlert={setAlert}
        type={alert.type}
      />
      <Header />
      <main className="appShellContent">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};
